import {
  CoachingDetailsInput,
  CoachingFormData,
  DeliveredStatusObject,
  enumEmpty,
} from '../../../api/CoachingForm/schema';
import { LegacyNote, Profile } from '../../../Types';
import { getLabelFromValue } from '../../CoachingForm/FieldComponents/Helpers';
import { getAllTopics } from '../../CoachingForm/CoachingDetails/Data';
import { IntlShape, useIntl } from 'react-intl';
import { getAllReasonOptions } from '../../CoachingForm/NoCoaching/Data';
import { DisplayObject } from '../../CoachingForm/LayoutComponents/FieldArrayDisplay';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';
import { Goal } from '@amzn/aet-galactus-kit';
import { clone } from 'lodash';

export interface ExpandableTableProps {
  rowCount: number;
  rowComponents: Record<string, any>[] | undefined;
  currentPage: number;
  currentSortColumn: string;
  currentSortDirection: string;
  isExpandable: boolean;
  children: React.ReactNode;
  pageName: string;
  noteId: string;
  coachingId?: string;
}

export interface CoachingHistoryProps {
  coachingHistory: CoachingFormData[];
  isTeam: boolean;
  legacyNotes: LegacyNote[];
  profile: Profile;
  profileState: string;
  page: string;
  refreshCoachingHistory: (
    queryVariablesCustomerCommentsNaws?: any,
    queryVariablesNawsCoachedNote?: any
  ) => void;
}

export interface TableRowData {
  status: string;
  lastUpdated: string;
  coach: string;
  createdAt: string;
  coaching: string[];
  data: CoachingFormData | LegacyNote;
  isLegacy: boolean;
  isExpandable: boolean;
  goals: Goal[];
}

export enum CoachingHistoryColumn {
  LAST_UPDATE = 'LAST_UPDATE',
  COACHING_TOPIC = 'COACHING_TOPIC',
  COACH = 'COACH',
  GOAL = 'GOAL',
  STATUS = 'STATUS',
  OPEN_DATE = 'OPEN_DATE',
  CLOSE_DATE = 'CLOSE_DATE',
  DELIVERED_DATE = 'DELIVERED_DATE',
  COMMENTS = 'COMMENTS',
  COACHING_ID = 'COACHING_ID',
  BUTTON_PLACEHOLDER = 'BUTTON_PLACEHOLDER',
  REVIEW_BUTTON = 'REVIEW_BUTTON',
  EDIT_BUTTON = 'EDIT_BUTTON',
}

export const sortDetail = (
  notes: CoachingFormData[],
  intl: IntlShape
): void => {
  notes.map(
    (note: CoachingFormData) =>
      'coachingDetails' in note &&
      clone(note.coachingDetails).sort(
        (a: CoachingDetailsInput, b: CoachingDetailsInput) => {
          if (
            getLabelFromValue(a.coachingTopic, getAllTopics(intl)) <
            getLabelFromValue(b.coachingTopic, getAllTopics(intl))
          ) {
            return -1;
          }
          if (
            getLabelFromValue(a.coachingTopic, getAllTopics(intl)) >
            getLabelFromValue(b.coachingTopic, getAllTopics(intl))
          ) {
            return 1;
          }
          return 0;
        }
      )
  );
};

export const parseTopic = (details: CoachingDetailsInput[]): string[] => {
  const topics: string[] = [];

  // eslint-disable-next-line array-callback-return
  details.map((detail: CoachingDetailsInput): void => {
    topics.push(
      getLabelFromValue(detail.coachingTopic, getAllTopics(useIntl())) as string
    );
  });

  return topics;
};

export const parseNotes = (
  notes: LegacyNote[],
  intl: IntlShape
): TableRowData[] =>
  notes.map(
    (note: LegacyNote): TableRowData => ({
      status:
        // Note: Current Resolver for dashboard has no status implemented since
        // it returns all coached notes, however for legacy notes, we need to
        // consider its status in Coaching History in Team
        'status' in note
          ? note.status === 'coached'
            ? intl.formatMessage({
                id: 'cs_perfportal_coaching_status_coached',
                defaultMessage: 'Coached',
              })
            : intl.formatMessage({
                id: 'cs_perfportal_coaching_status_not_coached',
                defaultMessage: 'Draft',
              })
          : intl.formatMessage({
              id: 'cs_perfportal_coaching_status_coached',
              defaultMessage: 'Coached',
            }),
      lastUpdated: note.updatedAt,
      createdAt: note.createdAt,
      coaching: [note.content],
      coach: note.author.username,
      data: note,
      isLegacy: true,
      isExpandable: false,
      goals: [],
    })
  );

export const parseHistory = (
  coachings: CoachingFormData[],
  intl: IntlShape
): TableRowData[] => {
  const parsedHistory: TableRowData[] = coachings.map(
    (note: CoachingFormData): TableRowData => ({
      status:
        'isDelivered' in note &&
        note.isDelivered === DeliveredStatusObject.DELIVERED
          ? intl.formatMessage({
              id: 'cs_perfportal_coaching_status_coached',
              defaultMessage: 'Coached',
            })
          : 'isDelivered' in note &&
              note.isDelivered === DeliveredStatusObject.OPEN_DELIVERED
            ? intl.formatMessage({
                id: 'cs_perfportal_coaching_status_open_delivered',
                defaultMessage: 'Open(Delivered)',
              })
            : note.isDelivered === DeliveredStatusObject.OPEN_REVIEWED
              ? intl.formatMessage({
                  id: 'cs_perfportal_coaching_status_open_reviewed',
                  defaultMessage: 'Open(Reviewed)',
                })
              : note.isDelivered === DeliveredStatusObject.CLOSED
                ? intl.formatMessage({
                    id: 'cs_perfportal_coaching_status_closed',
                    defaultMessage: 'Closed',
                  })
                : 'isDelivered' in note &&
                    note.isDelivered === DeliveredStatusObject.DRAFT
                  ? intl.formatMessage({
                      id: 'cs_perfportal_coaching_status_not_coached',
                      defaultMessage: 'Draft',
                    })
                  : intl.formatMessage({
                      id: 'cs_perfportal_coaching_status_no_session',
                      defaultMessage: 'No session',
                    }),
      lastUpdated: note.updatedAt,
      createdAt: note.createdAt,
      coaching:
        'reason' in note && note.reason !== enumEmpty
          ? [
              getLabelFromValue(
                note.reason,
                getAllReasonOptions(useIntl())
              ) as string,
            ]
          : 'coachingDetails' in note
            ? parseTopic(note.coachingDetails)
            : [],
      coach: note.deliveredBy?.userName ?? note.coach.userName,
      data: note,
      isLegacy: false,
      isExpandable: true,
      goals: note.goals ?? [],
    })
  );
  return parsedHistory;
};

export const convertLegacyNoteToDispaly = (
  content: string
): DisplayObject[] => {
  const display = [
    {
      label: '',
      value: content,
    },
  ];
  return display;
};

export const filterNoteForDashboard = (
  notes: TableRowData[]
): TableRowData[] => {
  const filteredNote: TableRowData[] = notes.filter(
    note =>
      ('isDelivered' in note.data &&
        (note.data.isDelivered === DeliveredStatusObject.DELIVERED ||
          note.data.isDelivered === DeliveredStatusObject.OPEN_DELIVERED ||
          note.data.isDelivered === DeliveredStatusObject.OPEN_REVIEWED ||
          note.data.isDelivered === DeliveredStatusObject.CLOSED)) ||
      'reason' in note.data
  );
  return filteredNote;
};

export const IsExpandableRow = (
  isCoachingNotePage: boolean,
  coachingNote: TableRowData
): boolean => {
  if (!coachingNote.isExpandable) return false;
  if (coachingNote.isLegacy) return false;

  // on coaching note page - display all coachings
  if (isCoachingNotePage) return true;

  // on dashboard page - display delivered coachings
  const caseManagementEnabled = isFeatureEnabled(Feature.CASE_MANAGEMENT);
  if (caseManagementEnabled) {
    switch ((coachingNote.data as CoachingFormData).isDelivered) {
      case DeliveredStatusObject.OPEN_DELIVERED:
        return isCoachingNotePage;
      case DeliveredStatusObject.OPEN_REVIEWED:
      case DeliveredStatusObject.CLOSED:
      case DeliveredStatusObject.DELIVERED: // for backward compatibility
      case DeliveredStatusObject.NOCOACH:
        return true;
      default:
        return false;
    }
  } else {
    switch ((coachingNote.data as CoachingFormData).isDelivered) {
      case DeliveredStatusObject.OPEN_DELIVERED: // for forward compatibility
      case DeliveredStatusObject.OPEN_REVIEWED: // for forward compatibility
      case DeliveredStatusObject.CLOSED: // for forward compatibility
      case DeliveredStatusObject.DELIVERED:
      case DeliveredStatusObject.NOCOACH:
        return true;
      default:
        return false;
    }
  }
};

export const showColumn = (
  column: CoachingHistoryColumn,
  isCoachingNotePage: boolean = false,
  coachingNote?: TableRowData
): boolean => {
  const caseManagementEnabled = isFeatureEnabled(Feature.CASE_MANAGEMENT);
  const goalSettingEnabled = isFeatureEnabled(Feature.GOAL_SETTING);
  // basic columns
  switch (column) {
    case CoachingHistoryColumn.LAST_UPDATE:
      return !caseManagementEnabled;
    case CoachingHistoryColumn.COACHING_TOPIC:
    case CoachingHistoryColumn.COACH:
    case CoachingHistoryColumn.STATUS:
      return true;
    case CoachingHistoryColumn.GOAL:
      return goalSettingEnabled;
    case CoachingHistoryColumn.OPEN_DATE:
    case CoachingHistoryColumn.CLOSE_DATE:
    case CoachingHistoryColumn.DELIVERED_DATE:
    case CoachingHistoryColumn.COMMENTS:
    case CoachingHistoryColumn.COACHING_ID:
      return caseManagementEnabled;
    case CoachingHistoryColumn.BUTTON_PLACEHOLDER:
      if (
        caseManagementEnabled &&
        !isCoachingNotePage &&
        (coachingNote?.data as CoachingFormData)?.isDelivered ===
          DeliveredStatusObject.OPEN_DELIVERED
      )
        return false; // display review button
      if (
        isCoachingNotePage &&
        (coachingNote?.data as CoachingFormData)?.isDelivered ===
          DeliveredStatusObject.DRAFT
      )
        return false; // display edit button
      return true;
    case CoachingHistoryColumn.REVIEW_BUTTON:
      if (
        caseManagementEnabled &&
        !isCoachingNotePage &&
        (coachingNote?.data as CoachingFormData)?.isDelivered ===
          DeliveredStatusObject.OPEN_DELIVERED
      )
        return true;
      return false;
    case CoachingHistoryColumn.EDIT_BUTTON:
      if (
        isCoachingNotePage &&
        (coachingNote?.data as CoachingFormData)?.isDelivered ===
          DeliveredStatusObject.DRAFT
      )
        return true;
      return false;
    default:
      return false;
  }
};

export const columnCount = (isCoachingNotePage: boolean): number => {
  let count = 0;
  for (const column in CoachingHistoryColumn) {
    if (
      (column as CoachingHistoryColumn) ===
        CoachingHistoryColumn.REVIEW_BUTTON ||
      (column as CoachingHistoryColumn) === CoachingHistoryColumn.EDIT_BUTTON
    )
      continue; // covered by BUTTON_ACTION already
    count += Number(
      showColumn(column as CoachingHistoryColumn, isCoachingNotePage)
    );
  }
  return count;
};

export const getExpandableContentColumnSpan = (
  isCoachingNotePage: boolean
): { content: number; padding: number } => {
  const totalColumn = columnCount(isCoachingNotePage);
  const caseManagementEnabled = isFeatureEnabled(Feature.CASE_MANAGEMENT);
  if (caseManagementEnabled) {
    return { content: totalColumn - 1, padding: 1 };
  } else {
    return { content: 1, padding: totalColumn - 2 };
  }
};
